import React, { useCallback, useMemo, useState } from 'react';
import { Alert, RefreshControl, StyleSheet, Text, View } from 'react-native';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { AppButton } from '../../components/AppButton';
import { AppCard } from '../../components/AppCard';
import { AppLoader } from '../../components/AppLoader';
import { AppScreen } from '../../components/AppScreen';
import { ErrorState } from '../../components/ErrorState';
import { PermissionGuard } from '../../components/PermissionGuard';
import { ScreenHeader } from '../../components/ScreenHeader';
import { UnauthorizedScreen } from '../system/UnauthorizedScreen';
import { PERMISSIONS } from '../../constants/permissions';
import type { RootStackParamList } from '../../constants/routes';
import { spacing } from '../../constants/spacing';
import { typography } from '../../constants/typography';
import { useAppColors } from '../../context/PreferencesContext';
import {
  ensureOrganizationPersonGroup,
  ensureOrganizationAwsProvider,
  getFaceOperationalStatus,
  getFaceSyncProblems,
  getOrganizationPersonGroup,
  runFacePrivacyCleanup,
  trainPersonGroupAndWait,
} from '../../services/faceAdminService';
import type {
  FacePrivacyCleanupResult,
  FaceSyncProblem,
  PersonGroupInfo,
} from '../../types/faceAdmin';
import type { FaceOperationalStatus } from '../../types/faceAttendance';
import { faceRegistrationErrorMessage } from '../../utils/faceRegistrationErrors';
import { normalizeApiError } from '../../api/apiError';

type Props = NativeStackScreenProps<RootStackParamList, 'FaceAdmin'>;

function providerLine(
  label: string,
  provider: FaceOperationalStatus['azureFace'],
): string {
  const parts = [
    label,
    provider.enabled == null ? null : provider.enabled ? 'enabled' : 'disabled',
    provider.connected == null
      ? null
      : provider.connected
        ? 'connected'
        : 'disconnected',
    provider.status,
    provider.message,
  ].filter(Boolean);
  return parts.join(' · ');
}

export function FaceAdminScreen({ navigation }: Props) {
  const palette = useAppColors();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [ops, setOps] = useState<FaceOperationalStatus | null>(null);
  const [personGroup, setPersonGroup] = useState<PersonGroupInfo | null>(null);
  const [problems, setProblems] = useState<FaceSyncProblem[]>([]);
  const [cleanup, setCleanup] = useState<FacePrivacyCleanupResult | null>(null);

  const themed = useMemo(
    () => ({
      section: {
        ...typography.sectionTitle,
        color: palette.textPrimary,
        marginBottom: spacing.sm,
      },
      body: { ...typography.body, color: palette.textSecondary },
      value: { ...typography.body, color: palette.textPrimary },
      meta: { ...typography.caption, color: palette.textMuted },
      info: { ...typography.caption, color: palette.info, marginBottom: spacing.md },
    }),
    [palette],
  );

  const load = useCallback(async (isRefresh = false) => {
    isRefresh ? setRefreshing(true) : setLoading(true);
    setError(null);
    try {
      const [opsResult, groupResult, syncResult] = await Promise.allSettled([
        getFaceOperationalStatus(),
        getOrganizationPersonGroup(),
        getFaceSyncProblems(),
      ]);
      if (opsResult.status === 'fulfilled') setOps(opsResult.value);
      else setOps(null);
      if (groupResult.status === 'fulfilled') setPersonGroup(groupResult.value);
      else setPersonGroup(null);
      if (syncResult.status === 'fulfilled') setProblems(syncResult.value);
      else setProblems([]);

      const failures = [opsResult, groupResult, syncResult].filter(
        item => item.status === 'rejected',
      );
      if (failures.length === 3) {
        setError('Unable to load face admin status.');
      } else if (failures.length) {
        setStatusMessage('Some face admin sections could not be loaded.');
      } else {
        setStatusMessage(null);
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  React.useEffect(() => {
    load().catch(() => undefined);
  }, [load]);

  async function ensureGroup() {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const result = await ensureOrganizationPersonGroup();
      setPersonGroup(result.personGroup);
      setStatusMessage(
        result.createdLocally || result.createdInAzure
          ? 'Person group ensured.'
          : 'Person group already ready.',
      );
    } catch (caught) {
      setError(
        faceRegistrationErrorMessage(normalizeApiError(caught).code),
      );
    } finally {
      setBusy(false);
    }
  }

  async function configureAwsProvider() {
    if (busy) return;
    setBusy(true);
    setError(null);
    setStatusMessage('Configuring an isolated AWS face collection…');
    try {
      const result = await ensureOrganizationAwsProvider();
      setStatusMessage(
        result.created
          ? 'AWS face collection created and synchronized.'
          : 'AWS face collection is configured and synchronized.',
      );
      await load(true);
    } catch (caught) {
      const apiError = normalizeApiError(caught);
      setError(faceRegistrationErrorMessage(apiError.code, apiError.message));
      setStatusMessage(null);
    } finally {
      setBusy(false);
    }
  }

  async function trainAndWait() {
    if (busy) return;
    setBusy(true);
    setError(null);
    setStatusMessage('Training person group…');
    try {
      const result = await trainPersonGroupAndWait();
      setStatusMessage(
        `Training ${result.trainingStatus}${
          result.durationMs != null ? ` in ${result.durationMs}ms` : ''
        }.`,
      );
      await load(true);
    } catch (caught) {
      setError(
        faceRegistrationErrorMessage(normalizeApiError(caught).code),
      );
    } finally {
      setBusy(false);
    }
  }

  function confirmCleanup() {
    Alert.alert(
      'Privacy cleanup',
      'Sanitize expired verification logs, liveness sessions, and sync errors per retention policy?',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Run cleanup',
          style: 'destructive',
          onPress: async () => {
            if (busy) return;
            setBusy(true);
            setError(null);
            try {
              const result = await runFacePrivacyCleanup();
              setCleanup(result);
              setStatusMessage('Privacy cleanup completed.');
            } catch (caught) {
              setError(
                faceRegistrationErrorMessage(normalizeApiError(caught).code),
              );
            } finally {
              setBusy(false);
            }
          },
        },
      ],
    );
  }

  if (loading && !ops && !personGroup) {
    return (
      <PermissionGuard
        permission={PERMISSIONS.FACE_REGISTER}
        fallback={<UnauthorizedScreen />}
      >
        <AppScreen contentStyle={styles.center}>
          <ScreenHeader
            title="Face admin"
            onBack={navigation.goBack}
          />
          <AppLoader message="Loading face admin" />
        </AppScreen>
      </PermissionGuard>
    );
  }

  return (
    <PermissionGuard
      permission={PERMISSIONS.FACE_REGISTER}
      fallback={<UnauthorizedScreen />}
    >
      <AppScreen
        scroll
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={() => load(true)}
            tintColor={palette.primary}
          />
        }
      >
        <ScreenHeader
          title="Face admin"
          onBack={navigation.goBack}
        />

        <View style={styles.body}>

          {error ? (
            <ErrorState message={error} onRetry={() => load()} />
          ) : null}
          {statusMessage ? (
            <Text accessibilityRole="alert" style={themed.info}>
              {statusMessage}
            </Text>
          ) : null}

          <Text style={themed.section}>Operational status</Text>
          <AppCard style={styles.card}>
            {ops ? (
              <>
                <Text style={themed.value}>
                  {providerLine('Azure Face', ops.azureFace)}
                </Text>
                <Text style={themed.value}>
                  {providerLine('AWS Rekognition', ops.awsRekognition)}
                </Text>
              </>
            ) : (
              <Text style={themed.body}>Operational status unavailable.</Text>
            )}
            <AppButton
              title="Configure AWS Face"
              onPress={configureAwsProvider}
              loading={busy}
              disabled={busy}
            />
          </AppCard>

          <Text style={themed.section}>Azure Person Group</Text>
          <AppCard style={styles.card}>
            {personGroup ? (
              <>
                <Text style={themed.value}>
                  Status: {personGroup.groupStatus || '—'}
                </Text>
                <Text style={themed.body}>
                  Training: {personGroup.trainingStatus || '—'}
                </Text>
                <Text style={themed.body}>
                  Sync: {personGroup.syncStatus || '—'}
                </Text>
                <Text style={themed.meta}>
                  Provider: {personGroup.biometricProvider || '—'}
                </Text>
              </>
            ) : (
              <Text style={themed.body}>
                Not configured. This is only required when the organization uses Azure Face.
              </Text>
            )}
            <View style={styles.actions}>
              <AppButton
                title="Ensure Person Group"
                onPress={ensureGroup}
                loading={busy}
                disabled={busy}
              />
              <AppButton
                title="Train & Wait"
                variant="secondary"
                onPress={trainAndWait}
                loading={busy}
                disabled={busy}
              />
            </View>
          </AppCard>

          <Text style={themed.section}>Sync problems</Text>
          <AppCard style={styles.card}>
            {problems.length ? (
              problems.slice(0, 20).map((item, index) => (
                <Text
                  key={`${item.entityType}-${item.entityId}-${index}`}
                  style={themed.body}
                >
                  {[
                    item.entityType,
                    item.employeeId != null ? `emp ${item.employeeId}` : null,
                    item.syncStatus,
                    item.errorCode,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </Text>
              ))
            ) : (
              <Text style={themed.body}>No sync problems reported.</Text>
            )}
          </AppCard>

          <Text style={themed.section}>Privacy cleanup</Text>
          <AppCard style={styles.card}>
            {cleanup ? (
              <Text style={themed.body}>
                Logs {cleanup.verificationLogsSanitized}, liveness{' '}
                {cleanup.livenessSessionsSanitized}, sync errors{' '}
                {cleanup.syncErrorsCleared.registrations +
                  cleanup.syncErrorsCleared.faces +
                  cleanup.syncErrorsCleared.personGroups}
              </Text>
            ) : (
              <Text style={themed.body}>
                Runs retention cleanup for expired face verification and liveness
                data.
              </Text>
            )}
            <AppButton
              title="Run Privacy Cleanup"
              variant="secondary"
              onPress={confirmCleanup}
              loading={busy}
              disabled={busy}
            />
          </AppCard>
        </View>
      </AppScreen>
    </PermissionGuard>
  );
}

const styles = StyleSheet.create({
  center: { justifyContent: 'center' },
  body: { marginTop: spacing.lg },
  card: { gap: spacing.sm, marginBottom: spacing.md },
  actions: { gap: spacing.sm, marginTop: spacing.sm },
});
